import React from 'react'
import { Button } from '@mui/material'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { useToasts } from 'react-toast-notifications'
import { describeFailure } from '../api/failureReason'
import FailureMessage from './FailureMessage'
import { useMedicalReport } from '../api/useMedicalReport'
import { advancedColors } from '../theme'

const MedicalReportButton = ({ uid }: { uid: string }) => {
  const { exportMedicalReport, loading } = useMedicalReport(uid)
  const { addToast } = useToasts()

  const open = async () => {
    try {
      const { url, warning } = await exportMedicalReport()

      if (warning) {
        addToast(<div style={{ whiteSpace: 'pre-line' }}>{warning}</div>, {
          appearance: 'warning',
          autoDismissTimeout: null,
        })
      }

      // Locally the server writes the PDF to its own disk and returns a path, not a URL.
      if (process.env.NODE_ENV === 'development') {
        addToast(`Medical report generated successfully: ${url}`, { appearance: 'success', autoDismissTimeout: 5000 })
      } else {
        window.open(url, '_blank')
      }
    } catch (error) {
      addToast(<FailureMessage failure={describeFailure('Failed to generate the medical report.', error)} />, {
        appearance: 'error',
      })
    }
  }

  return (
    <Button
      variant="contained"
      size="small"
      startIcon={<OpenInNewRoundedIcon />}
      onClick={open}
      disabled={loading}
      sx={{
        flexShrink: 0,
        px: 1.5,
        color: advancedColors.surface,
        backgroundColor: advancedColors.green,
        // `greenHover` with white type is about 3.7:1, so the press state is meru green.
        '&:hover': { color: advancedColors.surface, backgroundColor: advancedColors.deep },
        '&.Mui-disabled': { color: advancedColors.surface, backgroundColor: advancedColors.green },
      }}
    >
      {loading ? 'Generating…' : 'Open medical report'}
    </Button>
  )
}

export default MedicalReportButton
